import makeStyles from '@mui/styles/makeStyles';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import { FormattedMessage } from 'react-intl';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import { Link as RouterLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { OrganizationRole } from '../../models/organizationRole';
import { profile, resolvePath, sso, useQuery } from '../../routes';
import { organizationManagement, userManagement } from '../../layout/routes';
import { useConfig } from '../../config';
import { fromAuth } from '../../store';

const useStyles = makeStyles(theme => ({
  root: {
    paddingLeft: theme.spacing(4.5),
    paddingRight: theme.spacing(4.5),
    paddingTop: theme.spacing(3),
    height: '100%',
  },
  title: {
    fontWeight: theme.typography.fontWeightBold,
    textTransform: 'uppercase',
    fontSize: theme.spacing(2),
    color: '#516393',
  },
  item: {
    padding: theme.spacing(1, 0),
    fontSize: theme.spacing(2.5),
    cursor: 'pointer',
    color: '#172B4D',
  },
}));

const isAdminOrOwnerRole = (role: OrganizationRole) => (
  [OrganizationRole.ADMIN, OrganizationRole.OWNER].includes(role)
);

const isAdminOrOwnerOrBillingRole = (role: OrganizationRole) => (
  [OrganizationRole.ADMIN, OrganizationRole.BILLING_MANAGER, OrganizationRole.OWNER].includes(role)
);

interface QuickActionsProps {
  role: OrganizationRole | undefined;
  orgId: number;
  accountId: number | undefined;
}

const QuickActions = (props: QuickActionsProps) => {
  const { role, orgId, accountId } = props;
  const classes = useStyles();
  const { config } = useConfig();
  const viewKsLicenseLink = `${config?.testOpsPublicUrl}/user/license`;
  const generateApiKeyLink = `${config?.onpremise ? process.env.REACT_APP_ADMIN_URL : config?.testOpsPublicUrl}/user/apikey`;
  const viewProfileLink = config?.onpremise
    ? resolvePath(sso, undefined, { redirect_uri: `${process.env.REACT_APP_ADMIN_URL}/user/profile` })
    : resolvePath(profile);
  const { queryDictionary } = useQuery();
  const isRootOrAdmin = useSelector(fromAuth.selectIsRootOrAdmin);

  return (
    <Paper elevation={0} className={classes.root}>
      <Typography variant="subtitle2" className={classes.title}>
        <FormattedMessage id="home.quick_actions" />
      </Typography>
      <List component="ul">
        {((role && isAdminOrOwnerOrBillingRole(role)) || isRootOrAdmin) && (
          (
            <ListItem
              className={classes.item}
              component={({ innerRef, ...props }) => (
                <RouterLink {...props} to={resolvePath(sso, undefined, { redirect_uri: `${process.env.REACT_APP_ADMIN_URL}/organization/${orgId}/admin/license_keys/kse` })} />
              )}
              underline="none"
            >
              <FormattedMessage id="home.quick_actions.view_your_license" />
            </ListItem>
          )
        )}
        {((role && isAdminOrOwnerRole(role)) || isRootOrAdmin)
          && (
            <ListItem
              className={classes.item}
              component={RouterLink}
              to={resolvePath(userManagement, undefined, { ...queryDictionary(), view: 'invite_user' })}
            >
              <FormattedMessage id="home.quick_actions.invite_user" />
            </ListItem>
          )}
        {((role && isAdminOrOwnerRole(role)) || isRootOrAdmin)
          && (
            <>
              <ListItem
                className={classes.item}
                component={RouterLink}
                to={resolvePath(sso, undefined, { redirect_uri: `${process.env.REACT_APP_ADMIN_URL}/organization/${orgId}/admin/license_keys/kse` })}
              >
                <FormattedMessage id="home.quick_actions.assign_katalon_licenses" />
              </ListItem>
              <ListItem
                className={classes.item}
                component={RouterLink}
                to={resolvePath(
                  organizationManagement,
                  undefined,
                  { orgId: Number(orgId), accountId: Number(accountId) },
                )}
              >
                <FormattedMessage id="home.quick_actions.get_org_id" />
              </ListItem>
            </>
          )}

        { (role === OrganizationRole.MEMBER && !config?.onpremise)
          && (
            <ListItem
              className={classes.item}
              component={RouterLink}
              to={resolvePath(sso, undefined, { redirect_uri: viewKsLicenseLink })}
            >
              <FormattedMessage id="home.quick_actions.view_personal_ks_licenses" />
            </ListItem>
          )}
        <ListItem
          className={classes.item}
          component={RouterLink}
          to={resolvePath(sso, undefined, { redirect_uri: generateApiKeyLink })}

        >
          <FormattedMessage id="home.quick_actions.generate_api_key" />
        </ListItem>
        <ListItem
          className={classes.item}
          component={RouterLink}
          to={viewProfileLink}
        >
          <FormattedMessage id="home.quick_actions.manage_my_profile" />
        </ListItem>
      </List>
    </Paper>
  );
};

export default QuickActions;
